'use client';

import { useEffect } from 'react';
import type { TripOverviewItem } from '~/services/engine-ts';
import { ITINERARY_ITEM_ID_PREFIX, useOverviewProgressStore } from '~/stores/tpv2/overviewProgressStore';
import { buildItemRouteProgress, routeProgressAtFocus } from '../lib/cardRouteProgress';

export const ITINERARY_ACTIVE_LINE_PX = 120;
export const ITINERARY_SECTION_ID = 'overview-itinerary';

export const ITINERARY_FOCUS_LINE_RATIO = 0.4;

export function useItineraryScrollProgress(
  items: TripOverviewItem[] | undefined,
  deps: ReadonlyArray<unknown>,
  sectionId = ITINERARY_SECTION_ID
): void {
  const setProgress = useOverviewProgressStore((s) => s.setProgress);
  const setActiveItemId = useOverviewProgressStore((s) => s.setActiveItemId);
  const overlayOpen = useOverviewProgressStore((s) => s.overlayOpen);

  useEffect(() => {
    if (globalThis.window === undefined) return;
    if (overlayOpen || !items) return;

    const routeProgress = buildItemRouteProgress(items);

    let rafId = 0;
    let lastPublished: number | null = null;
    let lastActiveId: string | null = null;

    const compute = () => {
      rafId = 0;
      const section = document.getElementById(sectionId);
      if (!section) return;

      const focusLine = Math.max(ITINERARY_ACTIVE_LINE_PX, window.innerHeight * ITINERARY_FOCUS_LINE_RATIO);
      const cards = Array.from(section.querySelectorAll<HTMLElement>(`[id^="${ITINERARY_ITEM_ID_PREFIX}"]`), (card) => {
        const rect = card.getBoundingClientRect();
        return { id: card.id.slice(ITINERARY_ITEM_ID_PREFIX.length), start: rect.top, end: rect.bottom };
      });
      const { activeId, progress } = routeProgressAtFocus(cards, focusLine, routeProgress);

      if (progress !== lastPublished) {
        lastPublished = progress;
        setProgress(progress);
      }

      if (activeId !== lastActiveId) {
        lastActiveId = activeId;
        setActiveItemId(activeId);
      }
    };

    const schedule = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(compute);
    };

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    schedule();

    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [items, ...deps, overlayOpen, sectionId]);
}
